import type { Appointment } from '../types';

/**
 * Shape of the encounter summary embedded on AppointmentResponse. The OpenAPI
 * schema types `encounter` as `Any`, so the fields we rely on are typed here
 * and read defensively — older payloads may omit `document_summary`, and only
 * a positive recording count should escalate the delete warning.
 */
interface EmbeddedEncounterSummary {
  encounter_id?: string | null;
  transcripts_deleted?: boolean | null;
  document_summary?: {
    input_counts?: {
      counts?: Record<string, number> | null;
      total?: number | null;
    } | null;
  } | null;
}

export const getEmbeddedEncounter = (
  appointment: Appointment,
): EmbeddedEncounterSummary | null =>
  appointment.encounter && typeof appointment.encounter === 'object'
    ? (appointment.encounter as EmbeddedEncounterSummary)
    : null;

/**
 * Number of recording inputs captured on the appointment's linked encounter,
 * per the encounter summary's `document_summary`. Returns 0 when there is no
 * linked encounter, its transcripts were already purged, or the summary is
 * absent from the payload.
 */
export const getLinkedEncounterRecordingCount = (appointment: Appointment): number => {
  const encounter = getEmbeddedEncounter(appointment);
  if (!encounter?.encounter_id || encounter.transcripts_deleted) {
    return 0;
  }
  return encounter.document_summary?.input_counts?.counts?.recording ?? 0;
};

/**
 * True when deleting this appointment would strand an encounter that already
 * has recorded content (GDN-2325). The backend soft-deletes the appointment
 * only — the encounter and its recording/note are kept but stay linked to the
 * deleted appointment, so the recorded session silently drops off the
 * schedule with no in-product recovery path.
 */
export const appointmentHasRecordedEncounter = (appointment: Appointment): boolean =>
  getLinkedEncounterRecordingCount(appointment) > 0;
